import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import QuizResultsView from '../../components/QuizResultsView';
import { MyTestDetail, answerLabel, myTestApi, showTime } from '../../utils/myTestApi';

/**
 * One test, after it is done: submitted, and the result when the teacher's
 * setting allows (`results_visible`, decided by the API). Until then the
 * page says when, or that the teacher will release them -- and shows no
 * score, because the API sends none.
 */
const MyTestResult: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const just = (location.state as any) || {};
  const [t, setT] = useState<MyTestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    myTestApi
      .get(Number(id))
      .then(r => setT(r.result))
      .catch(e => setError(e.message));
  }, [id]);

  if (error) {
    return (
      <div className="container py-5" style={{ maxWidth: 720 }}>
        <div className="alert alert-warning">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/my-tests')}>Back to my tests</button>
      </div>
    );
  }
  if (!t) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const done = t.state === 'submitted' || t.state === 'auto_submitted';
  const r = t.result;

  const submittedNotice = just.justSubmitted && (
    <div className={`alert ${just.auto ? 'alert-warning' : 'alert-success'}`}>
      {just.auto ? 'Time was up, so your test was submitted with the answers you gave.' : 'Your test has been submitted.'}
    </div>
  );

  // Once the result is visible, the same screen as a custom quiz, with the
  // marks the server scored (this test's own marking, not a fixed +4/-1).
  if (r) {
    const questions = r.questions.map(q => ({
      id: q.id,
      question_order: q.question_order,
      question_img_url: q.content?.question_img_url,
      user_answer: q.user_answer ? answerLabel(q.user_answer, q.question_type) : null,
      correct_answer: answerLabel(q.correct_answer, q.question_type),
      status: (!q.user_answer ? 'unanswered' : q.is_correct ? 'correct' : 'incorrect') as 'correct' | 'incorrect' | 'unanswered',
      time_taken: q.time_spent || 0,
      score: q.marks_obtained,
    }));
    const gained = r.questions.reduce((sum, q) => sum + Math.max(0, q.marks_obtained), 0);
    const lost = r.questions.reduce((sum, q) => sum + Math.max(0, -q.marks_obtained), 0);
    return (
      <QuizResultsView
        subtitle={<>{t.title}{t.submitted_at ? ` • Submitted ${showTime(t.submitted_at)}` : ''}</>}
        questions={questions}
        totalQuestions={r.totals.total_questions}
        percentage={Math.round(r.percentage)}
        totalScore={r.score}
        scoreBreakdown={`+${gained} correct, -${lost} incorrect · out of ${r.totals.total_marks}`}
        notice={<>
          {submittedNotice}
          {t.max_attempts > 1 && <div className="alert alert-light border">Your best attempt is shown.</div>}
        </>}
        actions={<>
          <button className="btn btn-outline-primary btn-lg" style={{ borderRadius: '15px' }} onClick={() => navigate('/my-tests')}>
            <i className="fas fa-home me-2"></i>
            Back to My Tests
          </button>
          {t.in_progress && (
            <button className="btn btn-outline-info btn-lg" style={{ borderRadius: '15px' }} onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>
              <i className="fas fa-play me-2"></i>
              Continue Test
            </button>
          )}
          {!t.in_progress && t.can_start && (
            <button className="btn btn-outline-info btn-lg" style={{ borderRadius: '15px' }} onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>
              <i className="fas fa-redo me-2"></i>
              Try Again ({t.attempts_used} of {t.max_attempts} used)
            </button>
          )}
          <button className="btn btn-outline-secondary btn-lg" style={{ borderRadius: '15px' }} onClick={() => window.print()}>
            <i className="fas fa-print me-2"></i>
            Print Results
          </button>
        </>}
      />
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/my-tests')}>
        ← My tests
      </button>
      <h3 className="mb-1">{t.title}</h3>
      <div className="text-muted mb-4">
        {t.submitted_at ? `Submitted ${showTime(t.submitted_at)}` : `${showTime(t.scheduled_start)} – ${showTime(t.scheduled_end)}`}
      </div>

      {submittedNotice}

      {t.in_progress && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <span>You have this test open.</span>
          <button className="btn btn-primary" onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>Continue</button>
        </div>
      )}
      {!t.in_progress && t.can_start && (
        <div className="alert alert-light border d-flex justify-content-between align-items-center">
          <span>
            You can take this test again ({t.attempts_used} of {t.max_attempts} used). Your best score counts.
          </span>
          <button className="btn btn-primary" onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>Try again</button>
        </div>
      )}
      {t.state === 'missed' && <div className="alert alert-warning">You did not take this test before it closed.</div>}
      {t.state === 'excused' && <div className="alert alert-light border">Your teacher excused you from this test.</div>}

      {done && !t.results_visible && (
        <div className="card shadow-sm">
          <div className="card-body">
            <h5>Results</h5>
            <p className="mb-0 text-muted">
              {t.show_results_mode === 'after_window' && t.results_available_at
                ? `Your result will be shown here after the test closes for everyone, on ${showTime(t.results_available_at)}.`
                : t.show_results_mode === 'manual'
                ? 'Your teacher will release the results. Check back later.'
                : 'Your teacher has chosen not to share results for this test.'}
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default MyTestResult;
